import type { StatusTone } from '../../../components/design-system/admin';
import type { QueueBatchSummary, QueueJob, QueueStats, QueueType, StatusFilter } from './queue-monitor.types';

/** 850ms / 4.2s / 3.1m. */
export const formatDuration = (ms: number) => {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
};

/** Finished (completed or failed) share of a batch, 0–100. */
export const getProgressPercent = (batch: QueueBatchSummary) => {
    if (batch.totalJobs === 0) return 0;
    return ((batch.completed + batch.failed) / batch.totalJobs) * 100;
};

/** Jobs still waiting or running in a batch: only those can be cancelled. */
export const isBatchRunning = (batch: QueueBatchSummary) =>
    batch.pending > 0 || (batch.processing || 0) > 0 || (batch.generating_seamless || 0) > 0;

export const filterJobs = (jobs: QueueJob[], statusFilter: StatusFilter) => {
    switch (statusFilter) {
        case 'active':
            return jobs.filter((j) => j.status === 'processing' || j.status === 'generating_seamless');
        case 'completed':
            return jobs.filter((j) => j.status === 'completed');
        case 'failed':
            return jobs.filter((j) => j.status === 'failed');
        case 'waiting':
            return jobs.filter((j) => j.status === 'pending');
        default:
            return jobs;
    }
};

export const getStatusCount = (stats: QueueStats | null, status: StatusFilter) => {
    if (!stats) return 0;
    switch (status) {
        case 'active':
            return stats.summary.processing + stats.summary.generating_seamless;
        case 'completed':
            return stats.summary.completed;
        case 'failed':
            return stats.summary.failed;
        case 'waiting':
            return stats.summary.pending;
        case 'stuck':
            return stats.stuckJobs?.length ?? 0;
        default:
            return stats.summary.total;
    }
};

/** A batch's jobs: seamless "batches" are patterns, bulk ones are import batches. */
export const jobsOfBatch = (jobs: QueueJob[], batchId: string, queue: QueueType) =>
    jobs.filter((job) => (queue === 'seamless' ? job.pattern === batchId : job.batchId === batchId));

const STATUS: Record<string, { tone: StatusTone; key: string }> = {
    pending: { tone: 'warning', key: 'ADMIN_QUEUE.STATUS.PENDING' },
    processing: { tone: 'info', key: 'ADMIN_QUEUE.STATUS.PROCESSING' },
    generating_seamless: { tone: 'info', key: 'ADMIN_QUEUE.STATUS.GENERATING' },
    completed: { tone: 'success', key: 'ADMIN_QUEUE.STATUS.COMPLETED' },
    failed: { tone: 'danger', key: 'ADMIN_QUEUE.STATUS.FAILED' },
};

/** Tone and label key for a job status; unknown statuses read as themselves, neutral. */
export const jobStatusMeta = (status?: string): { tone: StatusTone; key?: string; raw: string } => {
    const known = status ? STATUS[status] : undefined;
    return { tone: known?.tone ?? 'neutral', key: known?.key, raw: (status || '').replace(/_/g, ' ') };
};
